import { useState, useEffect } from 'react'
import { Container } from '../../../../components'
import {
  Box,
  Divider,
  Stack,
  Typography,
} from '@elphasecure/elpha-design-system'
import { SmallLogoColored, SaaSInvoice } from '../../../../assets/svgs'
import { useSearchParams } from 'react-router-dom'
import { useGetSaaSBillingInfo } from '../api/useGetSaaSBillingInfo'
import Confirmation from '../components/Confirmation'
import Invoice from '../components/Invoice'
import { useDebounce } from '../../../../hooks'

export const SaaS = () => {
  const [activeStep, setActiveStep] = useState(0)
  const [postalCode, setPostalCode] = useState('')
  const [postalCodeError, setPostalCodeError] = useState(false)
  const debouncedPostal = useDebounce(postalCode, 1000)

  const [saasBillingInfoRes, setSaasBillingInfoRes] = useState({
    payment_details: {
      gross_cost: 0,
      taxes: 0,
      fees: 0,
    },
    client_secret: '',
  })
  const [searchParams] = useSearchParams()
  const {
    mutateAsync: getSaaSBillingInfo,
    isLoading: isLoadingBillingInfo,
    error: isErrorBillingInfo,
  } = useGetSaaSBillingInfo()
  const getLicensesBusiness = searchParams.get('licenses')
  const getOrgBusiness = searchParams.get('org')
  const getEmailBusiness = searchParams.get('email')
  const getPackageType = searchParams.get('package')

  useEffect(() => {
    if (isErrorBillingInfo) {
      setPostalCodeError(true)
    }
  }, [isErrorBillingInfo])

  useEffect(() => {
    let mounted = true
    let numberOfLicenses = getLicensesBusiness
      ? parseInt(getLicensesBusiness)
      : 10

    const getSaaSBilling = async () => {
      if (!mounted) return
      let res = await getSaaSBillingInfo({
        organization_name: getOrgBusiness,
        licenses: numberOfLicenses,
        package: getPackageType,
        email: getEmailBusiness,
        zipcode: debouncedPostal.length === 5 ? debouncedPostal : '',
      })
      setSaasBillingInfoRes(res)
    }

    getSaaSBilling()
    return () => {
      mounted = false
    }
  }, [
    getEmailBusiness,
    getLicensesBusiness,
    getPackageType,
    getOrgBusiness,
    getSaaSBillingInfo,
    debouncedPostal,
    postalCodeError,
  ])

  const billingInfo = {
    policy_details: {
      policyholder: {
        legal_entity_name: getOrgBusiness,
      },
    },
    payment: saasBillingInfoRes?.payment_details,
    client_secret: saasBillingInfoRes?.client_secret,
    licenseAmount: getLicensesBusiness,
    billingInfoIsLoading:
      saasBillingInfoRes?.client_secret === '' ? true : false,
  }

  const handleNext = async () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1)
  }

  const steps = [
    {
      title: `Purchase Elpha Secure ${
        getPackageType === 'pro' ? 'Pro' : 'Business+'
      }`,
      label: `After completing payment, you’ll receive an invite to your Elpha Secure cybersecurity dashboard.`,
      component: (
        <Invoice
          handleNext={handleNext}
          legalEntityName={
            billingInfo?.policy_details?.policyholder?.legal_entity_name
          }
          licenseAmount={billingInfo?.licenseAmount}
          policyData={billingInfo?.policy_details}
          paymentData={billingInfo?.payment}
          isLoading={billingInfo?.billingInfoIsLoading}
          isLoadingBillingInfo={isLoadingBillingInfo}
          clientSecret={billingInfo?.client_secret}
          postalCode={postalCode}
          postalCodeError={postalCodeError}
          setPostalCode={setPostalCode}
          packageType={getPackageType === 'pro' ? 'Pro' : 'Business+'}
        />
      ),
    },
    {
      title: 'Your Software Is Ready',
      label: `Payment is complete and your Elpha Secure ${
        getPackageType === 'pro' ? 'Pro' : 'Business+'
      } software is ready for installation.`,
      component: (
        <Confirmation
          isLoading={billingInfo?.billingInfoIsLoading}
          paymentData={billingInfo?.payment}
          legalEntityName={
            billingInfo?.policy_details?.policyholder?.legal_entity_name
          }
          licenseAmount={billingInfo?.licenseAmount}
        />
      ),
    },
  ]

  return (
    <Stack divider={<Divider orientation="horizontal" flexItem />}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          paddingY: '2.8rem',
        }}
      >
        <SmallLogoColored />
      </Box>
      {!billingInfo?.billingInfoIsLoading && (
        <>
          <Container maxWidth="lg" sx={{ paddingY: '8.0rem', flex: 1 }}>
            <Box sx={{ marginBottom: '4rem' }}>
              <Stack
                spacing="2.8rem"
                sx={{
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                }}
              >
                <SaaSInvoice />
                <Box>
                  <Typography
                    variant="headlineXLarge"
                    component="h1"
                    sx={{
                      fontWeight: '700',
                      color: 'primary.600',
                      paddingBottom: '1.2rem',
                    }}
                  >
                    {steps[activeStep].title}
                  </Typography>
                  <Typography
                    variant="bodyLarge"
                    sx={{
                      color: 'primary.400',
                      maxWidth: '70rem',
                      textAlign: 'center',
                    }}
                  >
                    {steps[activeStep].label}
                  </Typography>
                </Box>
              </Stack>
            </Box>
            {steps[activeStep].component}
          </Container>
        </>
      )}
    </Stack>
  )
}
